import { useMessage } from "@assistant-ui/react";
import { memo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { defaultComponents } from "@/components/assistant-ui/markdown-text";
import { cn } from "@/lib/utils";

/**
 * Custom text component that renders multi-agent responses as separate visual blocks
 * Splits content on "---" separator and renders each agent's response separately
 */
const MultiAgentTextImpl = () => {
  const message = useMessage();

  // Get text content from message
  const textContent = message.content.find((part) => part.type === "text");
  if (!textContent || textContent.type !== "text") {
    return null;
  }

  const text = textContent.text;

  // Split on the separator to get individual agent responses
  const agentResponses = text.split(/\n\n---\n\n/);

  // If only one response (no separators), render normally with react-markdown
  if (agentResponses.length === 1) {
    return (
      <div className="aui-md prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={defaultComponents}>
          {text}
        </ReactMarkdown>
      </div>
    );
  }

  // Render each agent response in a separate styled block
  return (
    <div className="flex flex-col gap-4">
      {agentResponses.map((response, index) => {
        // Extract agent name if it's in bold markdown format (**agent_name**)
        const agentMatch = response.match(/^\*\*(.+?)\*\*\n\n/);
        const agentName = agentMatch ? agentMatch[1] : null;
        const content = agentMatch ? response.replace(/^\*\*(.+?)\*\*\n\n/, "") : response;

        return (
          <div
            key={agentName ?? `agent-response-${index}`}
            className={cn(
              "rounded-lg p-4 transition-all",
              agentName
                ? "border-l-4 border-primary bg-muted/50 dark:bg-muted/30"
                : "bg-transparent"
            )}
          >
            {agentName && (
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-primary">
                {agentName}
              </div>
            )}
            <div className="aui-md prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={defaultComponents}>
                {content}
              </ReactMarkdown>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const MultiAgentText = memo(MultiAgentTextImpl);
